import type { ConsentDocument, ConsentKind } from "@ferrivia/api-contract";
import { Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { StepLayout } from "../components/StepLayout";
import { CONSENT_KINDS, type OnboardingConsentKind } from "../consentItems";
import s from "./ConsentsStep.module.css";

export interface ConsentsStepProps {
  value: Record<string, boolean>;
  documents: Partial<Record<ConsentKind, ConsentDocument>>;
  onToggle: (kind: OnboardingConsentKind, granted: boolean) => void;
  errorText?: string;
  kinds?: readonly OnboardingConsentKind[];
}

/**
 * Three checkboxes where the Glide app shows two. health_data_processing is
 * its own line rather than folded into the terms because GDPR Art. 7(2)
 * wants a consent request kept "clearly distinguishable from other matters",
 * and Art. 9(2)(a) explicit consent is what carries our health-data
 * processing until a physician is employed.
 *
 * Real checkbox inputs, not styled buttons: this is the one screen where a
 * client is agreeing to something, and the control should be exactly what it
 * appears to be to every assistive technology.
 */
export function ConsentsStep({ value, documents, onToggle, errorText, kinds = CONSENT_KINDS }: ConsentsStepProps) {
  const { t } = useTranslation("client");

  // i18next-parser only extracts literal-key t() calls, so these are static
  // lookups rather than template keys — same pattern as TabBar's nav labels.
  const labels: Record<OnboardingConsentKind, string> = {
    terms_and_privacy: t(
      "onboarding.consents.items.terms_and_privacy.label",
      "Hyväksyn Ferrivian käyttöehdot sekä tietosuojaselosteen.",
    ),
    health_data_processing: t(
      "onboarding.consents.items.health_data_processing.label",
      "Annan nimenomaisen suostumukseni terveyttäni koskevien tietojen käsittelyyn palvelun tuottamiseksi.",
    ),
    lab_partner_result_upload: t(
      "onboarding.consents.items.lab_partner_result_upload.label",
      "Jos ostan laboratoriomittauksia Ferrivian kautta, hyväksyn, että laboratoriokumppani voi ladata tulokseni suoraan Ferrivian alustalle.",
    ),
  };
  const linkLabels: Record<OnboardingConsentKind, string> = {
    terms_and_privacy: t("onboarding.consents.items.terms_and_privacy.linkLabel", "Käyttöehdot"),
    health_data_processing: t(
      "onboarding.consents.items.health_data_processing.linkLabel",
      "Tietosuojaseloste",
    ),
    lab_partner_result_upload: t(
      "onboarding.consents.items.lab_partner_result_upload.linkLabel",
      "Laboratoriotulosten siirto",
    ),
  };

  return (
    <StepLayout
      title={t("onboarding.consents.title", "Vielä hyväksynnät")}
      subtitle={t("onboarding.consents.subtitle", "Tarvitsemme nämä kolme, jotta voimme palvella sinua.")}
    >
      <ul className={s.list}>
        {kinds.map((kind) => {
          const doc = documents[kind];
          return (
            <li key={kind} className={s.item}>
              <label className={s.row}>
                <input
                  type="checkbox"
                  className={s.checkbox}
                  checked={value[kind] ?? false}
                  onChange={(e) => onToggle(kind, e.target.checked)}
                />
                <span className={s.label}>{labels[kind]}</span>
              </label>
              {doc ? (
                <a className={s.link} href={doc.url} target="_blank" rel="noreferrer">
                  {linkLabels[kind]}
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
      {errorText ? (
        <Text as="p" size="sm" className={s.error} role="alert">
          {errorText}
        </Text>
      ) : null}
    </StepLayout>
  );
}
